// Canonical client-side normaliser for Supabase Storage hosts.
//
// Rewrites a Supabase Storage URL so its host matches the dedicated storage
// origin (`import.meta.env.VITE_SUPABASE_STORAGE_ORIGIN`). Live builds keep
// `VITE_SUPABASE_URL` on the raw project host for the Supabase client, while
// the storage origin is the branded custom domain (e.g.
// `https://api.supply-guard.io`). This turns a raw
// `<ref>.supabase.co/storage/...` URL — including the signed URLs that edge
// functions mint against the raw `SUPABASE_URL` project host (ENT-2806) — into
// a custom-domain URL. Local and ephemeral preview builds do not set a branded
// storage origin, so they intentionally use their runtime project URL.
//
// Originally shipped inline in `logoUrl.ts` (PR #901, ENT-1112) as a read-path
// defence against absolute logo URLs that drifted across environments after a
// DB import. Extracted here (ENT-2806) so the document sanitizer can reuse the
// exact same rewrite without pulling in the Supabase client that `logoUrl.ts`
// imports. `logoUrl.ts` re-exports it for its existing callers.
//
// Signature safety: Supabase signs the object path + `token` query param, not
// the host, and the custom domain fronts the same storage gateway — so
// swapping only the host leaves a signed URL valid.
export function rewriteSupabaseStorageHost(rawUrl: string): string {
  const STORAGE_PATTERN = /^https?:\/\/[a-z0-9][a-z0-9-]*\.supabase\.co(\/storage\/.*)$/i;
  const match = rawUrl.match(STORAGE_PATTERN);
  if (!match) return rawUrl;

  const runtimeUrl =
    (typeof import.meta !== 'undefined' &&
      (import.meta.env?.VITE_SUPABASE_STORAGE_ORIGIN || import.meta.env?.VITE_SUPABASE_URL)) ||
    '';
  if (!runtimeUrl) return rawUrl;

  // Use the runtime protocol AND host. A local-dev runtime of
  // `http://127.0.0.1:54321` must produce `http://...` even when the stale
  // input was `https://<prod-ref>.supabase.co/...` — preserving the source
  // scheme yields invalid TLS requests in local/preview.
  let parsed: URL;
  try {
    parsed = new URL(runtimeUrl);
  } catch {
    return rawUrl;
  }

  return `${parsed.protocol}//${parsed.host}${match[1]}`;
}
